import getTaskTemplate from '@/api/taskTemplates/getTaskTemplate.endpoint';
import getTaskTemplates from '@/api/taskTemplates/getTaskTemplates.endpoint';
import { tags } from '@/constants/Tags';
import useCustomHook from '@/hooks/useCustomHook';
import openAPIErrorNotification from '@/shared/APIErrorNotification/APIErrorNotification';
import { GlobalContext } from '@/shared/GlobalContext/GlobalContext';
import { SSOContext } from '@/shared/SSOContext/SSOContext';
import { useEffect, useContext } from 'react';

export default function useLibraryThemeDetail({ theme }) {
  const context = useContext(GlobalContext);
  const ssoContext = useContext(SSOContext);

  const { state, actions } = useCustomHook({
    defaultState: {
      loading: true,
      showGrouped: false,
      templates: [],
      templatesData: {},
      selectedTemplate: null,
      selectedAction: null,
      groupedTemplates: {},
    },
    actions: {
      loadTaskTemplates: async () => {
        try {
          const templates = await getTaskTemplates(theme.id);

          for (const template of templates) {
            const [duration, description] = captureDuration(
              template.description
            );
            if (duration) {
              template.duration = duration;
              template.description = description;
            }
            template.description = paragraphsToArray(template.description);
          }

          let showGrouped = false;
          let groupedTemplates = {};

          if (
            context.state.selectedLanguage == 'es' &&
            process.env.NEXT_PUBLIC_TAGS_ENABLED
          ) {
            groupedTemplates = groupTemplatesByTag(templates);
            showGrouped = Object.values(groupedTemplates).some(
              (group) => group.length > 0
            );
          }

          actions.setState({
            templates,
            groupedTemplates,
            showGrouped,
            loading: false,
          });
        } catch (e) {
          console.error(e);
          actions.setState({ loading: false });
          openAPIErrorNotification({
            title: 'There was an error',
            description: 'There was a problem loading the tasks',
            errorFeedback: e.feedbackData,
          });
        }
      },
      loadTemplateData: async () => {
        try {
          actions.setState({ loading: true });
          const templateData = await getTaskTemplate(state.selectedTemplate.id);
          templateData.taskActivities = templateData.taskTemplateActivities;
          delete templateData.id;
          const templatesData = {
            ...state.templatesData,
            [state.selectedTemplate.id]: templateData,
          };
          actions.setState({ templatesData, loading: false });
        } catch (e) {
          actions.setState({ loading: false });
          openAPIErrorNotification({
            title: 'There was an error',
            description: 'There was a problem loading the task',
            errorFeedback: e.feedbackData,
          });
        }
      },
      setSelectedAction: (selectedAction, selectedTemplate) => {
        actions.setState({ selectedAction, selectedTemplate });
      },
      cleanAction: () => {
        actions.setSelectedAction(null, null);
        ssoContext.actions.clearPendingRedirect();
      },
    },
  });

  useEffect(() => {
    if (
      context.state.loaded &&
      context.state.user?.teacher &&
      context.state.isTeacherPro &&
      context.state.isSubscriptionActive &&
      theme?.id
    ) {
      actions.loadTaskTemplates();
    }
  }, [context.state, theme?.id]);

  useEffect(() => {
    if (
      state.selectedTemplate &&
      !state.templatesData[state.selectedTemplate.id]
    ) {
      actions.loadTemplateData();
    }
  }, [state.selectedTemplate]);

  useEffect(() => {
    if (
      ssoContext.state.loaded &&
      ssoContext.state.pendingRedirect?.success &&
      ssoContext.state.pendingRedirect.action == 'assign'
    ) {
      actions.setSelectedAction(
        ssoContext.state.pendingRedirect.action,
        ssoContext.state.pendingRedirect.state.taskTemplate
      );
    }
  }, [ssoContext.state.loaded]);

  return { state, actions };
}

function captureDuration(description) {
  if (description) {
    const regex = /Estimated task time\s+(\d+)(?:-(\d+))?\s+minutes\./;
    const match = description.match(regex);
    if (match) {
      const minutes = match[2] ? `${match[1]}-${match[2]}` : match[1];
      const newDescription = description.replace(regex, '');
      return [minutes, newDescription];
    }
  }
  return [null, description];
}

function paragraphsToArray(description) {
  if (description) {
    const paragraphs = description.match(/<p>(.*?)<\/p>/gs);
    if (paragraphs) {
      return paragraphs.map((p) => p.replace(/<\/?p>/g, ''));
    }
  }
  return [description];
}

function groupTemplatesByTag(templates) {
  return Object.values(tags).reduce((acc, tag) => {
    acc[tag.id] = templates.filter((template) =>
      template.taskTemplateTags.some(
        (taskTemplateTag) => taskTemplateTag.tagId == tag.id
      )
    );
    return acc;
  }, {});
}
